import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Search } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass } from '../ui';
interface Cliente {
  id: string;
  nombre: string;
}
interface Venta {
  id: string;
  numeroFactura: string;
  clienteId: string;
  clienteNombre: string;
  fecha: string;
  cantidad: number;
  precio: number;
  total: number;
  ruta: string;
  supervisor: string;
  observaciones: string;
}
interface VentasProps {
  ventas: Venta[];
  clientes: Cliente[];
  onAddVenta: (venta: Venta) => void;
  onDeleteVenta: (id: string) => void;
  onUpdateVenta: (venta: Venta) => void;
}
export function Ventas({
  ventas,
  clientes,
  onAddVenta,
  onDeleteVenta,
  onUpdateVenta
}: VentasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFecha, setFilterFecha] = useState('');
  const [formData, setFormData] = useState<Partial<Venta>>({
    cantidad: 0,
    precio: 0,
    total: 0
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleCalculateTotal = (cantidad: number, precio: number) => {
    const total = cantidad * precio;
    setFormData(prev => ({
      ...prev,
      cantidad,
      precio,
      total
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroFactura || !selectedCliente || !formData.fecha || !formData.cantidad || !formData.precio) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    if (editingId) {
      onUpdateVenta({
        ...formData,
        id: editingId,
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
      setEditingId(null);
    } else {
      onAddVenta({
        ...formData,
        id: Date.now().toString(),
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Venta);
    }
    setFormData({
      cantidad: 0,
      precio: 0,
      total: 0
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const handleEdit = (venta: Venta) => {
    setFormData(venta);
    setSelectedCliente(venta.clienteId);
    setEditingId(venta.id);
    setShowForm(true);
  };
  const filteredVentas = ventas.filter(v => {
    const matchSearch = v.numeroFactura.toLowerCase().includes(searchTerm.toLowerCase()) || v.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFecha = !filterFecha || v.fecha === filterFecha;
    return matchSearch && matchFecha;
  });
  const ventasHoy = ventas.filter(v => v.fecha === new Date().toISOString().split('T')[0]).length;
  const totalVentasDia = ventas.filter(v => v.fecha === new Date().toISOString().split('T')[0]).reduce((sum, v) => sum + v.total, 0);
  const totalVentas = ventas.reduce((sum, v) => sum + v.total, 0);
  const totalCantidad = ventas.reduce((sum, v) => sum + v.cantidad, 0);
  return <div className="space-y-6" data-id="element-942">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4" data-id="element-943">
        <div style={glass} data-id="element-944">
          <div className="text-sm text-gray-400" data-id="element-945">Total Ventas</div>
          <div className="text-3xl font-bold text-blue-400" data-id="element-946">
            {ventas.length}
          </div>
        </div>
        <div style={glass} data-id="element-947">
          <div className="text-sm text-gray-400" data-id="element-948">Hoy</div>
          <div className="text-3xl font-bold text-green-400" data-id="element-949">{ventasHoy}</div>
        </div>
        <div style={glass} data-id="element-950">
          <div className="text-sm text-gray-400" data-id="element-951">Monto Total</div>
          <div className="text-2xl font-bold text-yellow-400" data-id="element-952">
            RD$ {totalVentas.toFixed(2)}
          </div>
        </div>
        <div style={glass} data-id="element-953">
          <div className="text-sm text-gray-400" data-id="element-954">Cantidad Total (gal)</div>
          <div className="text-3xl font-bold text-purple-400" data-id="element-955">
            {totalCantidad}
          </div>
        </div>
      </div>

      {/* Controles */}
      <div style={glass} data-id="element-956">
        <div className="flex gap-4 flex-wrap" data-id="element-957">
          <div className="flex-1 min-w-[250px] relative" data-id="element-958">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" data-id="element-959" />
            <input type="text" placeholder="Buscar por factura o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 38
          }} data-id="element-960" />
          </div>
          <input type="date" value={filterFecha} onChange={e => setFilterFecha(e.target.value)} style={inputStyle} data-id="element-961" />

          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            cantidad: 0,
            precio: 0,
            total: 0
          });
          setSelectedCliente('');
        }} style={primaryButton} data-id="element-962">
            <Plus className="w-4 h-4" data-id="element-963" /> Registrar Venta
          </button>
        </div>
      </div>

      {/* Formulario */}
      {showForm && <div style={glass} data-id="element-964">
          <h3 className="text-lg font-bold mb-4" data-id="element-965">
            {editingId ? 'Editar Venta' : 'Registrar Nueva Venta'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-966">
            <div className="grid grid-cols-2 gap-4" data-id="element-967">
              <div data-id="element-968">
                <label className="text-sm text-gray-300" data-id="element-969">
                  Número de Factura *
                </label>
                <input type="text" value={formData.numeroFactura || ''} onChange={e => setFormData({
              ...formData,
              numeroFactura: e.target.value
            })} style={inputStyle} placeholder="FAC-2024-0001" data-id="element-970" />
              </div>
              <div data-id="element-971">
                <label className="text-sm text-gray-300" data-id="element-972">Fecha *</label>
                <input type="date" value={formData.fecha || ''} onChange={e => setFormData({
              ...formData,
              fecha: e.target.value
            })} style={inputStyle} data-id="element-973" />
              </div>
              <div data-id="element-974">
                <label className="text-sm text-gray-300" data-id="element-975">Cliente *</label>
                <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-976">
                  <option value="" data-id="element-977">Selecciona un cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id} data-id="element-978">
                      {c.nombre} ({c.codigo})
                    </option>)}
                </select>
              </div>
              <div data-id="element-979">
                <label className="text-sm text-gray-300" data-id="element-980">Ruta</label>
                <input type="text" value={formData.ruta || ''} onChange={e => setFormData({
              ...formData,
              ruta: e.target.value
            })} style={inputStyle} placeholder="Ruta Centro" data-id="element-981" />
              </div>
              <div data-id="element-982">
                <label className="text-sm text-gray-300" data-id="element-983">
                  Cantidad (galones) *
                </label>
                <input type="number" step="0.01" value={formData.cantidad || 0} onChange={e => handleCalculateTotal(parseFloat(e.target.value), formData.precio || 0)} style={inputStyle} placeholder="0" data-id="element-984" />
              </div>
              <div data-id="element-985">
                <label className="text-sm text-gray-300" data-id="element-986">
                  Precio por Galón *
                </label>
                <input type="number" step="0.01" value={formData.precio || 0} onChange={e => handleCalculateTotal(formData.cantidad || 0, parseFloat(e.target.value))} style={inputStyle} placeholder="0.00" data-id="element-987" />
              </div>
              <div data-id="element-988">
                <label className="text-sm text-gray-300" data-id="element-989">Total (RD$)</label>
                <input type="number" value={formData.total || 0} readOnly style={{
              ...inputStyle,
              opacity: 0.5
            }} data-id="element-990" />
              </div>
              <div data-id="element-991">
                <label className="text-sm text-gray-300" data-id="element-992">Supervisor</label>
                <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
              ...formData,
              supervisor: e.target.value
            })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-993" />
              </div>
              <div className="col-span-2" data-id="element-994">
                <label className="text-sm text-gray-300" data-id="element-995">Observaciones</label>
                <textarea value={formData.observaciones || ''} onChange={e => setFormData({
              ...formData,
              observaciones: e.target.value
            })} style={inputStyle} rows={3} placeholder="Notas adicionales..." data-id="element-996" />
              </div>
            </div>
            <div className="flex gap-2" data-id="element-997">
              <button type="submit" style={primaryButton} data-id="element-998">
                {editingId ? 'Actualizar' : 'Registrar'} Venta
              </button>
              <button type="button" onClick={() => {
            setShowForm(false);
            setEditingId(null);
          }} style={ghostButton} data-id="element-999">
                Cancelar
              </button>
            </div>
          </form>
        </div>}

      {/* Tabla de Ventas */}
      <div style={glass} data-id="element-1000">
        <h3 className="text-lg font-bold mb-4" data-id="element-1001">
          Registro de Ventas ({filteredVentas.length})
        </h3>
        <div className="overflow-x-auto" data-id="element-1002">
          <table className="w-full text-sm" data-id="element-1003">
            <thead data-id="element-1004">
              <tr className="border-b border-gray-700" data-id="element-1005">
                <th className="text-left py-2 px-2" data-id="element-1006">Factura</th>
                <th className="text-left py-2 px-2" data-id="element-1007">Fecha</th>
                <th className="text-left py-2 px-2" data-id="element-1008">Cliente</th>
                <th className="text-right py-2 px-2" data-id="element-1009">Cantidad (gal)</th>
                <th className="text-right py-2 px-2" data-id="element-1010">Precio</th>
                <th className="text-right py-2 px-2" data-id="element-1011">Total (RD$)</th>
                <th className="text-left py-2 px-2" data-id="element-1012">Ruta</th>
                <th className="text-left py-2 px-2" data-id="element-1013">Supervisor</th>
                <th className="text-center py-2 px-2" data-id="element-1014">Acciones</th>
              </tr>
            </thead>
            <tbody data-id="element-1015">
              {filteredVentas.length === 0 ? <tr data-id="element-1016">
                  <td colSpan={9} className="text-center py-4 text-gray-400" data-id="element-1017">
                    No hay ventas registradas
                  </td>
                </tr> : filteredVentas.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()).map(venta => <tr key={venta.id} className="border-b border-gray-700 hover:bg-gray-800/30" data-id="element-1018">
                      <td className="py-2 px-2 font-mono text-blue-400" data-id="element-1019">
                        {venta.numeroFactura}
                      </td>
                      <td className="py-2 px-2 text-sm" data-id="element-1020">{venta.fecha}</td>
                      <td className="py-2 px-2" data-id="element-1021">{venta.clienteNombre}</td>
                      <td className="py-2 px-2 text-right" data-id="element-1022">
                        {venta.cantidad.toFixed(2)}
                      </td>
                      <td className="py-2 px-2 text-right" data-id="element-1023">
                        {venta.precio.toFixed(2)}
                      </td>
                      <td className="py-2 px-2 text-right font-semibold text-green-400" data-id="element-1024">
                        {venta.total.toFixed(2)}
                      </td>
                      <td className="py-2 px-2 text-sm" data-id="element-1025">{venta.ruta || '-'}</td>
                      <td className="py-2 px-2 text-sm" data-id="element-1026">
                        {venta.supervisor || '-'}
                      </td>
                      <td className="py-2 px-2 text-center space-x-2" data-id="element-1027">
                        <button onClick={() => handleEdit(venta)} className="text-blue-400 hover:text-blue-300 inline-block" data-id="element-1028">
                          <Edit2 className="w-4 h-4" data-id="element-1029" />
                        </button>
                        <button onClick={() => onDeleteVenta(venta.id)} className="text-red-400 hover:text-red-300 inline-block" data-id="element-1030">
                          <Trash2 className="w-4 h-4" data-id="element-1031" />
                        </button>
                      </td>
                    </tr>)}
            </tbody>
          </table>
        </div>

        {/* Resumen */}
        {filteredVentas.length > 0 && <div className="border-t border-gray-700 mt-4 pt-4 grid grid-cols-4 gap-4" data-id="element-1032">
            <div data-id="element-1033">
              <div className="text-xs text-gray-400" data-id="element-1034">Cantidad Registros</div>
              <div className="text-lg font-bold text-blue-400" data-id="element-1035">
                {filteredVentas.length}
              </div>
            </div>
            <div data-id="element-1036">
              <div className="text-xs text-gray-400" data-id="element-1037">Total Galones</div>
              <div className="text-lg font-bold text-purple-400" data-id="element-1038">
                {filteredVentas.reduce((sum, v) => sum + v.cantidad, 0).toFixed(2)}
              </div>
            </div>
            <div data-id="element-1039">
              <div className="text-xs text-gray-400" data-id="element-1040">Monto Total</div>
              <div className="text-lg font-bold text-green-400" data-id="element-1041">
                RD${' '}
                {filteredVentas.reduce((sum, v) => sum + v.total, 0).toFixed(2)}
              </div>
            </div>
            <div data-id="element-1042">
              <div className="text-xs text-gray-400" data-id="element-1043">Precio Promedio</div>
              <div className="text-lg font-bold text-yellow-400" data-id="element-1044">
                {(filteredVentas.reduce((sum, v) => sum + v.precio, 0) / filteredVentas.length).toFixed(2)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}